import type { RescueRequiredDocument } from '../../lib/documentQueries';
import { certificateName, uploadedJustNow } from '../../lib/documentLabels';
import { FileCard } from '../Documents/FileCard';
import StatusPill from '../Documents/StatusPill';
import { formatDate } from '../../lib/utils';
import styles from './OrderDocumentsStep.module.scss';

/** Named so the note that follows reads as a sentence about this document. */
function noun(entry: RescueRequiredDocument): string {
  if (entry.documentType === 'NONPROFIT_LETTER') {
    return 'letter';
  }
  return certificateName(entry.state);
}

/**
 * What went in with this order, on the confirmation.
 *
 * Says the next order waits before the rescue discovers it: a first order goes through on a
 * document merely filed, and the one after it does not.
 */
export default function SubmittedDocuments({ required }: { required: RescueRequiredDocument[] }) {
  const awaiting = required.filter((entry) => entry.document?.status === 'PENDING');
  if (awaiting.length === 0) {
    return null;
  }

  return (
    <div className={styles.submitted}>
      {awaiting.map((entry) => {
        const doc = entry.document;
        if (!doc) {
          return null;
        }

        return (
          <FileCard
            key={`${entry.documentType}:${entry.state ?? ''}`}
            name={doc.originalFileName}
            sub={`Submitted for review · ${uploadedJustNow(doc) ? 'just now' : formatDate(doc.uploadedAt)}`}
          >
            <StatusPill tone="pending">In review</StatusPill>
          </FileCard>
        );
      })}

      <p className={styles.fileNote}>
        Your next order can be placed once the Fi team approves{' '}
        {awaiting.length === 1 ? `this ${noun(awaiting[0])}` : 'these documents'}.
      </p>
    </div>
  );
}
